import { BrainTopic } from "../config/mqtt_config";
import MqttInterface, {
  type BrokerCredentials,
  type FleetControlRequestHandler,
  type FleetPositionHandler,
  type MqttConnectionState,
} from "./mqtt_interface";
import SocketInterface from "./socket_interface";
import type {
  AuthCredentials,
  ConnectionState,
  MergedVehicleInfo,
  MqttRegisterState,
  SignalingVehicleInfo,
  SignalingVehicleStatus,
  VehiclePosition,
  VehicleSessionConnectResult,
  VehicleSessionSnapshot,
  VehicleSessionState,
  VehicleTransport,
  VehicleTransportState,
} from "./auth_types";

export type { BrokerCredentials };

export type AuthorizedVehicleInfo = {
  car: string;
  state: string;
};

export type BridgeConnectionSnapshot = {
  signalingState: ConnectionState;
  mqttState: ConnectionState;
  signalingError: string | null;
  mqttError: string | null;
  mqttRegisterState: MqttRegisterState;
  mqttRegisterError: string | null;
};

type MqttPayloadHandler = (payload: Uint8Array) => void;
type MqttIntentMap = Map<BrainTopic, Set<MqttPayloadHandler>>;
type MqttAttachedMap = Map<BrainTopic, Map<MqttPayloadHandler, () => void>>;
type OutgoingMqttIntentMap = Map<BrainTopic, Set<MqttPayloadHandler>>;
type StateListener = (snapshot: BridgeConnectionSnapshot) => void;
type VehicleSessionListener = (snapshot: VehicleSessionSnapshot) => void;

function formatError(err: unknown): Error {
  if (err instanceof Error) {
    return err;
  }
  return new Error(String(err));
}

// 차량 MQTT 세션 복구 — 브로커가 살아난 뒤 차량 토픽/핸들러를 다시 붙일 때의 재시도 정책.
const MQTT_SESSION_RECOVERY_RETRY_MS = 1500;
const MQTT_SESSION_RECOVERY_MAX_ATTEMPTS = 10;

function createEmptyVehicleSessionSnapshot(): VehicleSessionSnapshot {
  return {
    vehicleId: null,
    sessionState: "idle",
    mqttTransportState: "idle",
    signalingTransportState: "idle",
    recovering: false,
    remoteVideoReady: false,
    mqttError: null,
    signalingError: null,
    activeTransports: [],
  };
}

function cloneVehicleSessionSnapshot(
  snapshot: VehicleSessionSnapshot,
): VehicleSessionSnapshot {
  return {
    ...snapshot,
    activeTransports: [...snapshot.activeTransports],
  };
}

function isMqttVehicleReady(state: string | null): boolean {
  return state === "IDLE";
}

function isSignalingVehicleReady(
  status: SignalingVehicleStatus | null,
): boolean {
  return status === "available";
}

function getVehicleReason(
  mqttState: string | null,
  signalingStatus: SignalingVehicleStatus | null,
  mqttConnectable: boolean,
  signalingConnectable: boolean,
): string {
  if (mqttConnectable && signalingConnectable) {
    return "both ready";
  }

  if (mqttConnectable) {
    return "MQTT ready only";
  }

  if (signalingConnectable) {
    return "signaling ready only";
  }

  if (signalingStatus === "busy") {
    return "signaling busy";
  }

  if (signalingStatus === "unavailable") {
    return "signaling unavailable";
  }

  if (mqttState && mqttState !== "IDLE") {
    return "MQTT not idle";
  }

  return "no available transport";
}

export function mergeVehicleLists(
  mqttVehicles: AuthorizedVehicleInfo[],
  signalingVehicles: SignalingVehicleInfo[],
  connectionSnapshot: Pick<BridgeConnectionSnapshot, "mqttState" | "signalingState">,
  positions?: ReadonlyMap<string, VehiclePosition>,
): MergedVehicleInfo[] {
  const byVehicleId = new Map<string, MergedVehicleInfo>();
  const mqttServiceReady = connectionSnapshot.mqttState === "connected";
  const signalingServiceReady = connectionSnapshot.signalingState === "connected";

  signalingVehicles.forEach((vehicle) => {
    const signalingConnectable =
      signalingServiceReady && isSignalingVehicleReady(vehicle.status);
    byVehicleId.set(vehicle.vehicleId, {
      vehicleId: vehicle.vehicleId,
      mqttState: null,
      signalingStatus: vehicle.status,
      mqttConnectable: false,
      signalingConnectable,
      connectableTransports: signalingConnectable ? ["signaling"] : [],
      selectable: signalingConnectable,
      reason: getVehicleReason(null, vehicle.status, false, signalingConnectable),
    });
  });

  mqttVehicles.forEach((vehicle) => {
    const vehicleId = vehicle.car;
    const existing = byVehicleId.get(vehicleId);
    const mqttConnectable = mqttServiceReady && isMqttVehicleReady(vehicle.state);
    const signalingConnectable =
      Boolean(existing?.signalingConnectable) ||
      (signalingServiceReady &&
        isSignalingVehicleReady(existing?.signalingStatus || null));
    const connectableTransports: VehicleTransport[] = [];
    if (mqttConnectable) {
      connectableTransports.push("mqtt");
    }
    if (signalingConnectable) {
      connectableTransports.push("signaling");
    }
    const next: MergedVehicleInfo = {
      vehicleId,
      mqttState: vehicle.state,
      signalingStatus: existing?.signalingStatus || null,
      mqttConnectable,
      signalingConnectable,
      connectableTransports,
      selectable: connectableTransports.length > 0,
      reason: getVehicleReason(
        vehicle.state,
        existing?.signalingStatus || null,
        mqttConnectable,
        signalingConnectable,
      ),
    };
    byVehicleId.set(vehicleId, next);
  });

  // 전차량 실시간 위치 병합 — 위치를 보고한 차량만 position 을 가진다.
  if (positions && positions.size > 0) {
    byVehicleId.forEach((vehicle, vehicleId) => {
      const position = positions.get(vehicleId);
      if (position) {
        vehicle.position = position;
      }
    });
  }

  return Array.from(byVehicleId.values()).sort((a, b) =>
    a.vehicleId.localeCompare(b.vehicleId),
  );
}

export default class SocketMqttBridge {
  socket: SocketInterface;
  mqtt: MqttInterface | null;
  config: any;
  mqttConnecting: Promise<void> | null;
  mqttDisconnecting: Promise<void> | null;
  mqttUserId: string | null;
  mqttBrokerPassword: string | null;
  mqttVehicleId: string | null;
  mqttRegisteredUserId: string | null;
  mqttRegisterState: MqttRegisterState;
  mqttRegisterError: string | null;
  mqttVehicleListRequest: Promise<AuthorizedVehicleInfo[]> | null;
  mqttSubscriptions: MqttIntentMap;
  mqttAttachedSubscriptions: MqttAttachedMap;
  mqttOutgoingSubscriptions: OutgoingMqttIntentMap;
  private fleetPositions: Map<string, VehiclePosition>;
  private fleetPositionUnsubscribe: (() => void) | null;
  private mqttSessionRecoveryTimer: ReturnType<typeof setTimeout> | null = null;
  private mqttSessionRecovering = false;
  private mqttSessionRecoveryAttempts = 0;
  signalingState: ConnectionState;
  mqttState: ConnectionState;
  signalingError: string | null;
  mqttError: string | null;
  storedCredentials: AuthCredentials | null;
  signalingVehicleCache: SignalingVehicleInfo[];
  private mqttDisconnectRequestUnsubscribe: (() => void) | null;
  private mqttPublishUnsubscribe: (() => void) | null;
  private mqttConnectionStateUnsubscribe: (() => void) | null;
  private stateListeners: Set<StateListener>;
  private vehicleSession: VehicleSessionSnapshot;
  private vehicleSessionListeners: Set<VehicleSessionListener>;
  private requestedVehicleTransports: VehicleTransport[];

  constructor(socket: SocketInterface, config: any = {}) {
    this.socket = socket;
    this.config = config;
    this.mqtt = null;
    this.mqttConnecting = null;
    this.mqttDisconnecting = null;
    this.mqttUserId = null;
    this.mqttBrokerPassword = null;
    this.mqttVehicleId = null;
    this.mqttRegisteredUserId = null;
    this.mqttRegisterState = "idle";
    this.mqttRegisterError = null;
    this.mqttVehicleListRequest = null;
    this.mqttSubscriptions = new Map();
    this.mqttAttachedSubscriptions = new Map();
    this.mqttOutgoingSubscriptions = new Map();
    this.fleetPositions = new Map();
    this.fleetPositionUnsubscribe = null;
    this.signalingState = "idle";
    this.mqttState = "idle";
    this.signalingError = null;
    this.mqttError = null;
    this.storedCredentials = null;
    this.signalingVehicleCache = [];
    this.mqttDisconnectRequestUnsubscribe = null;
    this.mqttPublishUnsubscribe = null;
    this.mqttConnectionStateUnsubscribe = null;
    this.stateListeners = new Set();
    this.vehicleSession = createEmptyVehicleSessionSnapshot();
    this.vehicleSessionListeners = new Set();
    this.requestedVehicleTransports = [];

    if (typeof this.socket.registerEvent === "function") {
      this.socket.registerEvent("error", this.handleSocketError, this);
      this.socket.registerEvent("peerState", this.handleSocketPeerState, this);
      this.socket.registerEvent("onTrack", this.handleSocketTrack, this);
    }
  }

  setCredentials(credentials: AuthCredentials): void {
    this.storedCredentials = { ...credentials };
  }

  getCredentials(): AuthCredentials | null {
    return this.storedCredentials ? { ...this.storedCredentials } : null;
  }

  subscribeState(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.getConnectionSnapshot());
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  getConnectionSnapshot(): BridgeConnectionSnapshot {
    return {
      signalingState: this.signalingState,
      mqttState: this.mqttState,
      signalingError: this.signalingError,
      mqttError: this.mqttError,
      mqttRegisterState: this.mqttRegisterState,
      mqttRegisterError: this.mqttRegisterError,
    };
  }

  subscribeVehicleSession(listener: VehicleSessionListener): () => void {
    this.vehicleSessionListeners.add(listener);
    listener(this.getVehicleSessionSnapshot());
    return () => {
      this.vehicleSessionListeners.delete(listener);
    };
  }

  getVehicleSessionSnapshot(): VehicleSessionSnapshot {
    return cloneVehicleSessionSnapshot(this.vehicleSession);
  }

  async loginSignaling(): Promise<void> {
    if (!this.storedCredentials?.userId || !this.storedCredentials?.password) {
      throw new Error("Missing credentials for signaling login");
    }

    this.signalingState = "loading";
    this.signalingError = null;
    this.emitState();

    try {
      const result = await this.socket.login(this.storedCredentials);
      this.signalingVehicleCache = result.vehicleList || this.socket.getCachedVehicleList();
      this.signalingState = "connected";
      this.signalingError = null;
      this.emitState();
    } catch (err) {
      this.signalingState = "error";
      this.signalingError = formatError(err).message;
      this.emitState();
      throw formatError(err);
    }
  }

  async loginMqtt(): Promise<void> {
    if (!this.storedCredentials?.userId || !this.storedCredentials?.password) {
      throw new Error("Missing credentials for MQTT login");
    }

    this.mqttState = "loading";
    this.mqttError = null;
    this.emitState();

    try {
      await this.connectMqttBroker(this.storedCredentials);
      this.mqttState = "connected";
      this.mqttError = null;
      this.emitState();
    } catch (err) {
      this.mqttState = "error";
      this.mqttError = formatError(err).message;
      this.emitState();
      throw formatError(err);
    }
  }

  async loadSignalingVehicles(): Promise<SignalingVehicleInfo[]> {
    if (this.signalingState !== "connected") {
      return this.signalingVehicleCache;
    }

    try {
      const vehicles = await this.socket.listVehicles();
      this.signalingVehicleCache = vehicles;
      this.signalingError = null;
      this.emitState();
      return vehicles;
    } catch (err) {
      const cached = this.socket.getCachedVehicleList();
      if (cached.length > 0) {
        this.signalingVehicleCache = cached;
        this.emitState();
        return cached;
      }

      this.signalingState = "error";
      this.signalingError = formatError(err).message;
      this.emitState();
      throw formatError(err);
    }
  }

  loadMqttVehicles(): Promise<AuthorizedVehicleInfo[]> {
    return this.fetchAuthorizedVehicles();
  }

  async loadMergedVehicles(): Promise<MergedVehicleInfo[]> {
    const promises: Promise<any>[] = [];
    const mqttEnabled = this.mqttState === "connected";
    const signalingEnabled = this.signalingState === "connected";

    promises.push(
      mqttEnabled ? this.loadMqttVehicles() : Promise.resolve([] as AuthorizedVehicleInfo[]),
    );
    promises.push(
      signalingEnabled
        ? this.loadSignalingVehicles()
        : Promise.resolve(this.signalingVehicleCache),
    );

    const [mqttResult, signalingResult] = await Promise.allSettled(promises);
    const mqttVehicles =
      mqttResult.status === "fulfilled" ? mqttResult.value : ([] as AuthorizedVehicleInfo[]);
    const signalingVehicles =
      signalingResult.status === "fulfilled"
        ? signalingResult.value
        : this.signalingVehicleCache;

    if (mqttResult.status === "rejected") {
      if (!this.isMqttConnected()) {
        this.mqttState = "error";
        this.mqttError = formatError(mqttResult.reason).message;
      } else if (this.mqttRegisterState !== "error") {
        this.mqttError = formatError(mqttResult.reason).message;
      }
    } else if (mqttEnabled) {
      this.mqttError = null;
    }

    if (signalingResult.status === "rejected") {
      this.signalingState = "error";
      this.signalingError = formatError(signalingResult.reason).message;
    }

    this.emitState();
    return mergeVehicleLists(
      mqttVehicles,
      signalingVehicles,
      {
        mqttState: this.mqttState,
        signalingState: this.signalingState,
      },
      this.fleetPositions,
    );
  }

  async connectVehicleSession(
    vehicleId: string,
  ): Promise<VehicleSessionConnectResult> {
    const mergedVehicles = await this.loadMergedVehicles();
    const selectedVehicle = mergedVehicles.find(
      (vehicle) => vehicle.vehicleId === vehicleId,
    );

    if (!selectedVehicle || !selectedVehicle.selectable) {
      throw new Error("No available transport for the selected vehicle");
    }

    const requestedTransports = [...selectedVehicle.connectableTransports];
    this.beginVehicleSession(vehicleId, requestedTransports);

    const attemptByTransport = new Map<
      VehicleTransport,
      Promise<VehicleTransport>
    >();

    if (selectedVehicle.mqttConnectable) {
      attemptByTransport.set(
        "mqtt",
        this.preconnectMqtt(vehicleId)
          .then(() => {
            this.markVehicleTransportConnected("mqtt");
            return "mqtt" as const;
          })
          .catch((err) => {
            this.markVehicleTransportFailed("mqtt", formatError(err).message);
            throw formatError(err);
          }),
      );
    }

    if (selectedVehicle.signalingConnectable) {
      attemptByTransport.set(
        "signaling",
        this.socket
          .joinVehicle(vehicleId)
          .then(() => "signaling" as const)
          .catch((err) => {
            this.markVehicleTransportFailed(
              "signaling",
              formatError(err).message,
            );
            this.socket.disconnectVehicle();
            throw formatError(err);
          }),
      );
    }

    const settled = await Promise.allSettled(
      Array.from(attemptByTransport.entries()).map(async ([transport, promise]) => ({
        transport,
        value: await promise,
      })),
    );

    const successfulTransports = settled
      .filter(
        (
          result,
        ): result is PromiseFulfilledResult<{
          transport: VehicleTransport;
          value: VehicleTransport;
        }> => result.status === "fulfilled",
      )
      .map((result) => result.value.transport);

    if (successfulTransports.length === 0) {
      await Promise.allSettled([
        this.mqttVehicleId === vehicleId ? this.resetMqttVehicleSession(vehicleId) : Promise.resolve(),
        Promise.resolve().then(() => this.socket.disconnectVehicle()),
      ]);
      this.clearVehicleSession();
      const firstFailure = settled.find(
        (result): result is PromiseRejectedResult => result.status === "rejected",
      );
      throw formatError(firstFailure?.reason || "Vehicle connection failed");
    }

    return {
      vehicleId,
      sessionState: this.vehicleSession.sessionState,
      activeTransports: successfulTransports,
      mqttError: this.vehicleSession.mqttError,
      signalingError: this.vehicleSession.signalingError,
    };
  }

  async disconnectVehicleSession(): Promise<void> {
    const vehicleId = this.vehicleSession.vehicleId || this.mqttVehicleId;
    const shouldDisconnectMqtt = Boolean(vehicleId && this.mqttVehicleId === vehicleId);
    const shouldDisconnectSignaling =
      this.vehicleSession.signalingTransportState !== "idle" ||
      this.socket.isDataChannelOpen();

    this.clearVehicleSession();

    await Promise.allSettled([
      shouldDisconnectMqtt ? this.disconnectMqttVehicle() : Promise.resolve(),
      Promise.resolve().then(async () => {
        if (!shouldDisconnectSignaling) {
          return;
        }
        // WebRTC 정상 종료(나가기 시):
        // 시그널링 프로토콜에 "leave/bye" 메시지가 없어, peer(pc)만 닫으면 시그널링 WS 가
        // 열려 있는 한 차량(io_webrtc)이 콕핏이 떠난 것을 감지하지 못해 차량 상태가 정상으로
        // 복귀하지 않는다(로그아웃=WS 종료 시에만 복귀하던 증상). 따라서 세션을 완전히 닫아
        // (disconnectSession=WS 종료) 차량이 떠남을 감지하게 한 뒤, 차량 선택 목록을 위해
        // 시그널링을 곧바로 재연결한다(자격증명 보유 시). 재연결 실패는 차량 선택 화면에서
        // 재시도 가능하므로 조용히 무시한다.
        this.socket.disconnectSession();
        if (this.storedCredentials?.userId && this.storedCredentials?.password) {
          try {
            await this.loginSignaling();
          } catch (_err) {
            // 재연결 실패 — 조용히 무시(다음 화면에서 재시도).
          }
        }
      }),
    ]);
  }

  async reconnectSignalingVehicleSession(): Promise<void> {
    const vehicleId = this.vehicleSession.vehicleId;
    if (!vehicleId) {
      throw new Error("No active vehicle session to reconnect");
    }

    if (!this.requestedVehicleTransports.includes("signaling")) {
      this.requestedVehicleTransports = [
        ...this.requestedVehicleTransports,
        "signaling",
      ];
    }

    this.patchVehicleSession({
      signalingTransportState: "connecting",
      remoteVideoReady: false,
      signalingError: null,
    });

    // 시그 다운 복구는 "부분 재연결(peer 만 닫기)"로는 정상화되지 않고(실측: 로그아웃 후
    // 재접속해야만 정상), 로그아웃과 동일한 "전체 클리어"가 필요하다. 따라서 시그널링 세션을
    // 완전히 닫고(disconnectSession = WS + 모든 peer + data channel 정리) 자격증명은 유지한 채
    // 새로 로그인 → 차량 재합류해 WebRTC 를 처음부터 다시 맺는다.
    this.socket.disconnectSession();

    try {
      await this.loginSignaling();
      await this.socket.joinVehicle(vehicleId);
    } catch (err) {
      const error = formatError(err);
      this.markVehicleTransportFailed("signaling", error.message);
      throw error;
    }
  }

  disconnectSignaling(): void {
    this.socket.disconnectSession();
    this.signalingVehicleCache = [];
    this.signalingState = "idle";
    this.signalingError = null;
    if (this.vehicleSession.vehicleId) {
      this.markVehicleTransportFailed("signaling", "Signaling disconnected");
    }
    this.emitState();
  }

  connectMqttBroker(credentials: BrokerCredentials): Promise<void> {
    if (!credentials?.userId) {
      return Promise.reject(new Error("userId is required"));
    }

    if (credentials.password) {
      this.setCredentials({
        userId: credentials.userId,
        password: credentials.password,
      });
    }

    if (this.mqtt && this.mqtt.connected && this.mqttUserId === credentials.userId) {
      return Promise.resolve();
    }

    if (this.mqttConnecting) {
      return this.mqttConnecting.then(() => this.connectMqttBroker(credentials));
    }

    const password = credentials.password ?? this.storedCredentials?.password;
    if (!password) {
      return Promise.reject(new Error("MQTT password is required"));
    }

    if (this.mqttUserId && this.mqttUserId !== credentials.userId) {
      this.disconnectMqtt();
    }

    const brokerWsUrl = this.config?.mqtt_ws_url || this.config?.mqttWsUrl;
    if (!this.mqtt) {
      const mqttConfig: any = {};
      if (brokerWsUrl) {
        mqttConfig.brokerWsUrl = brokerWsUrl;
        mqttConfig.wsUrl = brokerWsUrl;
      }
      this.mqtt = new MqttInterface(mqttConfig);
      this.bindMqttConnectionStateHandler();
      this.bindMqttPublishHandler();
    }

    this.mqttUserId = credentials.userId;
    this.mqttBrokerPassword = password;

    const connectPromise = this.mqtt
      .connectBroker({
        userId: credentials.userId,
        password,
      })
      .then(() => {
        this.bindBrokerJsonHandlers();
        console.log(
          `[SocketMqttBridge] MQTT Broker Connected for user: ${credentials.userId}`,
        );
      })
      .catch((err) => {
        this.disconnectMqtt();
        throw err;
      });

    const trackedPromise = connectPromise.finally(() => {
      if (this.mqttConnecting === trackedPromise) {
        this.mqttConnecting = null;
      }
    });

    this.mqttConnecting = trackedPromise;
    return trackedPromise;
  }

  async fetchAuthorizedVehicles(): Promise<AuthorizedVehicleInfo[]> {
    await this.ensureMqttBrokerReady();

    if (!this.mqtt || !this.mqttUserId) {
      throw new Error("Broker not connected. Call connectMqttBroker first.");
    }

    const userId = this.mqttUserId;
    if (this.mqttVehicleListRequest) {
      return this.mqttVehicleListRequest;
    }

    const request = this.fetchAuthorizedVehiclesOnce(userId).finally(() => {
      if (this.mqttVehicleListRequest === request) {
        this.mqttVehicleListRequest = null;
      }
    });
    this.mqttVehicleListRequest = request;
    return request;
  }

  private fetchAuthorizedVehiclesOnce(
    userId: string,
  ): Promise<AuthorizedVehicleInfo[]> {
    return new Promise((resolve, reject) => {
      let settled = false;
      let waitingFor: "register" | "list" =
        this.mqttRegisteredUserId === userId ? "list" : "register";
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let unsubRegister: (() => void) | null = null;
      let unsubList: (() => void) | null = null;

      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (unsubRegister) {
          unsubRegister();
        }
        if (unsubList) {
          unsubList();
        }
      };

      const rejectOnce = (err: unknown) => {
        if (settled) {
          return;
        }
        settled = true;
        if (waitingFor === "register") {
          this.setMqttRegisterState("error", formatError(err).message);
        }
        cleanup();
        reject(formatError(err));
      };

      const resolveOnce = (list: AuthorizedVehicleInfo[]) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        resolve(list);
      };

      const sendListRequest = () => {
        waitingFor = "list";
        try {
          this.mqtt!.sendListRequest(userId);
        } catch (err) {
          rejectOnce(err);
        }
      };

      if (this.mqttRegisteredUserId !== userId) {
        this.setMqttRegisterState("registering");
        unsubRegister = this.mqtt!.onJsonMessage(
          `${userId}/register/complete`,
          (_topic, msg: any) => {
            if (
              msg?.requester !== userId ||
              msg?.messageType !== "register/complete"
            ) {
              return;
            }

            if (msg.result === "success") {
              this.mqttRegisteredUserId = userId;
              this.setMqttRegisterState("registered");
              sendListRequest();
              return;
            }

            rejectOnce(this.formatBrokerResultError("Register failed", msg));
          },
        );
      } else {
        this.setMqttRegisterState("registered");
      }

      unsubList = this.mqtt!.onJsonMessage(
        `${userId}/list/complete`,
        (_topic, msg: any) => {
          if (
            (msg?.requester && msg.requester !== userId) ||
            msg?.messageType !== "list/complete"
          ) {
            return;
          }

          if (msg.result === "success") {
            resolveOnce(Array.isArray(msg.list) ? msg.list : []);
            return;
          }

          rejectOnce(this.formatBrokerResultError("List request failed", msg));
        },
      );

      timeoutId = setTimeout(() => {
        rejectOnce(new Error("Vehicle list request timeout"));
      }, 5000);

      try {
        if (this.mqttRegisteredUserId === userId) {
          sendListRequest();
        } else {
          this.mqtt!.sendRegister(userId);
        }
      } catch (err) {
        rejectOnce(err);
      }
    });
  }

  async preconnectMqtt(vehicleId: string): Promise<void> {
    await this.ensureMqttBrokerReady();

    if (!this.mqtt || !this.mqttUserId) {
      throw new Error("Broker not connected. Call connectMqttBroker first.");
    }

    if (!vehicleId) {
      throw new Error("vehicleId is required");
    }

    if (this.mqttVehicleId === vehicleId) {
      return;
    }

    if (this.mqttVehicleId && this.mqttVehicleId !== vehicleId) {
      throw new Error(
        `Already connected to vehicle ${this.mqttVehicleId}. Disconnect first.`,
      );
    }

    const userId = this.mqttUserId;

    return new Promise((resolve, reject) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const cleanup = (unsubscribe?: () => void) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (unsubscribe) {
          unsubscribe();
        }
      };

      const unsubscribe = this.mqtt!.onJsonMessage(
        `${userId}/connect/complete`,
        async (_topic, msg: any) => {
          if (
            msg?.vehicleId !== vehicleId ||
            msg?.messageType !== "connect/complete"
          ) {
            return;
          }

          if (settled) {
            return;
          }
          settled = true;
          cleanup(unsubscribe);

          if (msg.result !== "success") {
            this.mqttVehicleId = null;
            reject(this.formatBrokerResultError("Vehicle connection denied", msg));
            return;
          }

          try {
            await this.mqtt!.subscribeVehicle(vehicleId);
            this.mqttVehicleId = vehicleId;
            this.attachPendingMqttSubscriptions();
            resolve();
          } catch (err) {
            this.mqttVehicleId = null;
            reject(formatError(err));
          }
        },
      );

      timeoutId = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup(unsubscribe);
        this.mqttVehicleId = null;
        reject(new Error("Vehicle connection timeout"));
      }, 5000);

      try {
        this.mqtt!.sendConnectVehicle(userId, vehicleId);
      } catch (err) {
        settled = true;
        cleanup(unsubscribe);
        this.mqttVehicleId = null;
        reject(formatError(err));
      }
    });
  }

  disconnectMqttVehicle(): Promise<void> {
    if (!this.mqttVehicleId || !this.mqttUserId || !this.mqtt) {
      return Promise.resolve();
    }

    if (this.mqttDisconnecting) {
      return this.mqttDisconnecting;
    }

    const userId = this.mqttUserId;
    const vehicleId = this.mqttVehicleId;

    const disconnectPromise = new Promise<void>((resolve, reject) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const cleanup = (unsubscribe?: () => void) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        if (unsubscribe) {
          unsubscribe();
        }
      };

      const unsubscribe = this.mqtt!.onJsonMessage(
        `${userId}/disconnect/complete`,
        (_topic, msg: any) => {
          if (
            msg?.vehicleId !== vehicleId ||
            msg?.messageType !== "disconnect/complete"
          ) {
            return;
          }

          if (settled) {
            return;
          }
          settled = true;
          cleanup(unsubscribe);

          if (msg.result === "success") {
            resolve();
            return;
          }

          reject(this.formatBrokerResultError("Vehicle disconnect failed", msg));
        },
      );

      timeoutId = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup(unsubscribe);
        reject(new Error("Vehicle disconnect timeout"));
      }, 5000);

      try {
        this.mqtt!.sendDisconnectVehicle(userId, vehicleId);
      } catch (err) {
        settled = true;
        cleanup(unsubscribe);
        reject(formatError(err));
      }
    }).finally(async () => {
      await this.resetMqttVehicleSession(vehicleId);
      if (this.vehicleSession.vehicleId === vehicleId) {
        this.markVehicleTransportFailed("mqtt", "MQTT vehicle session disconnected");
      }
    });

    const trackedPromise = disconnectPromise.finally(() => {
      if (this.mqttDisconnecting === trackedPromise) {
        this.mqttDisconnecting = null;
      }
    });

    this.mqttDisconnecting = trackedPromise;
    return trackedPromise;
  }

  disconnectMqtt(): void {
    this.detachAllAttachedMqttSubscriptions();
    this.fleetPositionUnsubscribe?.();
    this.fleetPositionUnsubscribe = null;
    this.fleetPositions.clear();
    this.mqttDisconnectRequestUnsubscribe?.();
    this.mqttDisconnectRequestUnsubscribe = null;
    this.mqttPublishUnsubscribe?.();
    this.mqttPublishUnsubscribe = null;
    this.mqttConnectionStateUnsubscribe?.();
    this.mqttConnectionStateUnsubscribe = null;

    if (this.mqttVehicleId && this.mqtt) {
      void this.mqtt.unsubscribeVehicle(this.mqttVehicleId).catch((err) => {
        console.error("[SocketMqttBridge] Failed to unsubscribe vehicle:", err);
      });
    }

    if (this.mqtt) {
      this.mqtt.disconnect();
    }

    this.mqtt = null;
    this.mqttConnecting = null;
    this.mqttDisconnecting = null;
    this.mqttUserId = null;
    this.mqttBrokerPassword = null;
    this.mqttVehicleId = null;
    this.mqttVehicleListRequest = null;
    this.resetMqttRegisterState();
    this.mqttState = "idle";
    this.mqttError = null;
    if (this.vehicleSession.vehicleId) {
      this.markVehicleTransportFailed("mqtt", "MQTT disconnected");
    }
    this.emitState();
  }

  isMqttConnected(): boolean {
    return this.mqtt != null && this.mqtt.connected;
  }

  sendMqtt(topic: BrainTopic, payload: Uint8Array): void {
    if (!this.mqtt || !this.mqtt.connected) {
      console.warn("[SocketMqttBridge] MQTT is not connected");
      return;
    }
    this.mqtt.publish(topic, payload);
  }

  onMqtt(topic: BrainTopic, handler: MqttPayloadHandler): () => void {
    let handlers = this.mqttSubscriptions.get(topic);
    if (!handlers) {
      handlers = new Set<MqttPayloadHandler>();
      this.mqttSubscriptions.set(topic, handlers);
    }
    handlers.add(handler);

    this.attachMqttHandler(topic, handler);

    return () => {
      const intentHandlers = this.mqttSubscriptions.get(topic);
      if (intentHandlers) {
        intentHandlers.delete(handler);
        if (intentHandlers.size === 0) {
          this.mqttSubscriptions.delete(topic);
        }
      }
      this.detachMqttHandler(topic, handler);
    };
  }

  onOutgoingMqtt(topic: BrainTopic, handler: MqttPayloadHandler): () => void {
    let handlers = this.mqttOutgoingSubscriptions.get(topic);
    if (!handlers) {
      handlers = new Set<MqttPayloadHandler>();
      this.mqttOutgoingSubscriptions.set(topic, handlers);
    }
    handlers.add(handler);

    return () => {
      const intentHandlers = this.mqttOutgoingSubscriptions.get(topic);
      if (intentHandlers) {
        intentHandlers.delete(handler);
        if (intentHandlers.size === 0) {
          this.mqttOutgoingSubscriptions.delete(topic);
        }
      }
    };
  }

  /**
   * 함대 차량의 제어권 요청 수신 구독.
   *
   * 위치와 같은 함대 토픽을 타므로 별도 구독이 필요 없다 — 이미 도착하는 메시지 중
   * 지금까지 버려지던 것을 받는 것뿐이다.
   */
  onFleetControlRequest(handler: FleetControlRequestHandler): () => void {
    if (!this.mqtt) {
      return () => undefined;
    }
    return this.mqtt.onFleetControlRequest(handler);
  }

  /**
   * 전차량 실시간 위치 수신(read-only) 구독.
   * 주어진 차량들의 STATE 토픽을 구독하고, 위치 업데이트를 받을 때마다
   * 내부 캐시(this.fleetPositions)에 누적하고 onUpdate 로 최신 스냅샷을 알린다.
   * 브로커 미연결 시에는 차량 목록만 기억해 두고 연결 후 자동 구독한다.
   * 반환된 함수를 호출하면 구독을 해제하고 캐시를 비운다.
   */
  subscribeFleetPositions(
    vehicleIds: string[],
    onUpdate?: (positions: ReadonlyMap<string, VehiclePosition>) => void,
  ): () => void {
    this.fleetPositionUnsubscribe?.();
    this.fleetPositionUnsubscribe = null;

    const handler: FleetPositionHandler = (vehicleId, position) => {
      this.fleetPositions.set(vehicleId, position);
      onUpdate?.(this.getFleetPositions());
    };

    if (this.mqtt) {
      const off = this.mqtt.onFleetPosition(handler);
      void this.mqtt
        .setFleetPositionVehicles(vehicleIds)
        .catch((err) => {
          console.error(
            "[SocketMqttBridge] Failed to subscribe fleet positions:",
            err,
          );
        });
      this.fleetPositionUnsubscribe = () => {
        off();
        this.mqtt?.clearFleetPositions();
      };
    } else {
      this.fleetPositionUnsubscribe = () => undefined;
    }

    return () => {
      this.fleetPositionUnsubscribe?.();
      this.fleetPositionUnsubscribe = null;
      this.fleetPositions.clear();
    };
  }

  getFleetPositions(): ReadonlyMap<string, VehiclePosition> {
    return new Map(this.fleetPositions);
  }

  private bindBrokerJsonHandlers(): void {
    this.mqttDisconnectRequestUnsubscribe?.();
    this.mqttDisconnectRequestUnsubscribe = null;

    if (!this.mqtt || !this.mqttUserId) {
      return;
    }

    this.mqttDisconnectRequestUnsubscribe = this.mqtt.onJsonMessage(
      `${this.mqttUserId}/disconnect/request`,
      (_topic, msg: any) => {
        const vehicleId =
          typeof msg?.vehicleId === "string" ? msg.vehicleId : this.mqttVehicleId;

        if (vehicleId) {
          try {
            this.mqtt!.sendDisconnectResponse(this.mqttUserId!, vehicleId);
          } catch (err) {
            console.error(
              "[SocketMqttBridge] Failed to send disconnect response:",
              err,
            );
          }
        }

        void this.handleRemoteDisconnect(msg);
      },
    );
  }

  private bindMqttPublishHandler(): void {
    this.mqttPublishUnsubscribe?.();
    this.mqttPublishUnsubscribe = null;

    if (!this.mqtt) {
      return;
    }

    this.mqttPublishUnsubscribe = this.mqtt.onPublish((topic, payload) => {
      const handlers = this.mqttOutgoingSubscriptions.get(topic);
      if (!handlers) {
        return;
      }

      handlers.forEach((handler) => {
        handler(payload);
      });
    });
  }

  private bindMqttConnectionStateHandler(): void {
    this.mqttConnectionStateUnsubscribe?.();
    this.mqttConnectionStateUnsubscribe = null;

    if (!this.mqtt) {
      return;
    }

    this.mqttConnectionStateUnsubscribe = this.mqtt.onConnectionState(
      (state, error) => {
        this.handleMqttConnectionState(state, error);
      },
    );
  }

  private handleMqttConnectionState(
    state: MqttConnectionState,
    error?: Error,
  ): void {
    if (!this.mqtt) {
      return;
    }

    if (state === "connected") {
      this.mqttState = "connected";
      this.mqttError = null;
      this.attachPendingMqttSubscriptions();
      // 브로커가 (재)연결됐다 — 끊겨 있던 차량 MQTT 세션도 되살린다.
      // 이게 없으면 콕핏 모니터링은 "MQTT DOWN" 에서 회복되지 않는다(새로고침 전까지).
      this.mqttSessionRecoveryAttempts = 0;
      this.scheduleMqttVehicleSessionRecovery();
      this.emitState();
      return;
    }

    if (state === "disconnected" || state === "reconnecting") {
      this.resetMqttRegisterState();
      this.mqttVehicleListRequest = null;
      this.mqttState = "loading";
      this.mqttError =
        state === "reconnecting" ? "MQTT reconnecting" : "MQTT disconnected";
      this.emitState();
      return;
    }

    this.resetMqttRegisterState();
    this.mqttVehicleListRequest = null;
    this.mqttState = "error";
    this.mqttError = error?.message || "MQTT connection error";
    this.emitState();
  }

  private async handleRemoteDisconnect(msg: any): Promise<void> {
    const vehicleId =
      typeof msg?.vehicleId === "string" ? msg.vehicleId : this.mqttVehicleId;
    await this.resetMqttVehicleSession(vehicleId);
    if (this.vehicleSession.vehicleId && this.vehicleSession.vehicleId === vehicleId) {
      this.markVehicleTransportFailed("mqtt", "MQTT vehicle session disconnected");
    }
  }

  private ensureMqttBrokerReady(): Promise<void> {
    if (this.mqtt && this.mqtt.connected && this.mqttUserId) {
      return Promise.resolve();
    }

    if (this.mqttConnecting) {
      return this.mqttConnecting;
    }

    return Promise.reject(
      new Error("Broker not connected. Call connectMqttBroker first."),
    );
  }

  private setMqttRegisterState(
    state: MqttRegisterState,
    error: string | null = null,
  ): void {
    this.mqttRegisterState = state;
    this.mqttRegisterError = error;
    this.emitState();
  }

  private resetMqttRegisterState(): void {
    this.mqttRegisteredUserId = null;
    this.mqttRegisterState = "idle";
    this.mqttRegisterError = null;
  }

  private formatBrokerResultError(prefix: string, msg: any): Error {
    const detailText = Array.isArray(msg?.details)
      ? msg.details.join(" ")
      : msg?.details || msg?.result || "unknown error";
    return new Error(`${prefix}: ${detailText}`);
  }

  private async resetMqttVehicleSession(vehicleId: string | null): Promise<void> {
    this.detachAllAttachedMqttSubscriptions();

    if (vehicleId && this.mqtt) {
      try {
        await this.mqtt.unsubscribeVehicle(vehicleId);
      } catch (err) {
        console.error("[SocketMqttBridge] Failed to unsubscribe vehicle:", err);
      }
    }

    this.mqttVehicleId = null;
  }

  private attachPendingMqttSubscriptions(): void {
    this.mqttSubscriptions.forEach((handlers, topic) => {
      handlers.forEach((handler) => {
        this.attachMqttHandler(topic, handler);
      });
    });
  }

  private attachMqttHandler(topic: BrainTopic, handler: MqttPayloadHandler): void {
    if (!this.mqtt || !this.mqtt.connected || !this.mqttVehicleId) {
      return;
    }

    let attachedByTopic = this.mqttAttachedSubscriptions.get(topic);
    if (!attachedByTopic) {
      attachedByTopic = new Map();
      this.mqttAttachedSubscriptions.set(topic, attachedByTopic);
    }

    if (attachedByTopic.has(handler)) {
      return;
    }

    const unsubscribe = this.mqtt.onMessage(topic, (_topic, payload) => {
      handler(payload);
    });
    attachedByTopic.set(handler, unsubscribe);
  }

  private detachMqttHandler(topic: BrainTopic, handler: MqttPayloadHandler): void {
    const attachedByTopic = this.mqttAttachedSubscriptions.get(topic);
    if (!attachedByTopic) {
      return;
    }

    const unsubscribe = attachedByTopic.get(handler);
    if (unsubscribe) {
      unsubscribe();
    }
    attachedByTopic.delete(handler);

    if (attachedByTopic.size === 0) {
      this.mqttAttachedSubscriptions.delete(topic);
    }
  }

  private detachAllAttachedMqttSubscriptions(): void {
    this.mqttAttachedSubscriptions.forEach((handlerMap) => {
      handlerMap.forEach((unsubscribe) => {
        unsubscribe();
      });
    });
    this.mqttAttachedSubscriptions.clear();
  }

  private handleSocketError(err?: unknown): void {
    if (!this.vehicleSession.vehicleId) {
      return;
    }
    this.markVehicleTransportFailed(
      "signaling",
      formatError(err || "Signaling session error").message,
    );
  }

  private handleSocketPeerState(state?: any): void {
    if (!this.vehicleSession.vehicleId) {
      return;
    }

    const iceConnectionState = String(state?.iceConnectionState || "");
    const connectionState = String(state?.connectionState || "");
    const dataChannelState = String(state?.dataChannelState || "");

    if (
      connectionState === "connected" ||
      iceConnectionState === "connected" ||
      iceConnectionState === "completed" ||
      dataChannelState === "open"
    ) {
      this.markVehicleTransportConnected("signaling");
      return;
    }

    if (
      dataChannelState === "closed" &&
      this.vehicleSession.signalingTransportState !== "connected"
    ) {
      this.markVehicleTransportFailed(
        "signaling",
        "Signaling data channel disconnected",
      );
      return;
    }

    if (
      connectionState === "failed" ||
      connectionState === "closed" ||
      iceConnectionState === "failed" ||
      iceConnectionState === "closed"
    ) {
      this.markVehicleTransportFailed(
        "signaling",
        `Signaling peer connection ${connectionState || iceConnectionState}`,
      );
    }
  }

  private handleSocketTrack(evt?: any): void {
    if (!this.vehicleSession.vehicleId) {
      return;
    }

    if (evt?.track?.kind === "video" && !this.vehicleSession.remoteVideoReady) {
      this.patchVehicleSession({
        remoteVideoReady: true,
      });
    }
  }

  private beginVehicleSession(
    vehicleId: string,
    requestedTransports: VehicleTransport[],
  ): void {
    this.requestedVehicleTransports = [...requestedTransports];
    this.vehicleSession = this.normalizeVehicleSession({
      vehicleId,
      sessionState: "connecting",
      mqttTransportState: requestedTransports.includes("mqtt")
        ? "connecting"
        : "idle",
      signalingTransportState: requestedTransports.includes("signaling")
        ? "connecting"
        : "idle",
      remoteVideoReady: false,
      mqttError: null,
      signalingError: null,
      activeTransports: [],
    });
    this.emitVehicleSession();
  }

  private clearVehicleSession(): void {
    this.clearMqttSessionRecoveryTimer();
    this.mqttSessionRecoveryAttempts = 0;
    this.requestedVehicleTransports = [];
    this.vehicleSession = createEmptyVehicleSessionSnapshot();
    this.emitVehicleSession();
  }

  /**
   * 끊겼던 차량 MQTT 세션을 다시 세운다.
   *
   * 배경: mqttTransportState 를 "failed" 로 내리는 경로는 여러 곳인데(브로커 끊김, 원격
   * disconnect 등) 다시 "connected" 로 되돌리는 곳은 최초 연결 흐름뿐이었다. 그래서 콕핏
   * 모니터링 화면은 한 번 "MQTT DOWN" 이 되면 새로고침(=세션 재생성) 전까지 회복되지
   * 않았다. 반면 차량 선택 화면은 브로커 상태(mqttState)를 보므로 자동 재연결로 회복됐다.
   * → 브로커가 살아나면 차량 토픽 구독/핸들러 부착까지 다시 수행하고 세션 상태를 복구한다.
   */
  private scheduleMqttVehicleSessionRecovery(): void {
    const vehicleId = this.vehicleSession.vehicleId;
    if (!vehicleId) {
      return;
    }
    if (this.vehicleSession.mqttTransportState === "connected") {
      return;
    }
    if (!this.requestedVehicleTransports.includes("mqtt")) {
      return;
    }
    if (!this.mqtt || !this.mqtt.connected) {
      return;
    }
    if (this.mqttSessionRecovering) {
      return;
    }
    if (this.mqttSessionRecoveryAttempts >= MQTT_SESSION_RECOVERY_MAX_ATTEMPTS) {
      // 복구 포기 — 세션을 비워 UI(useVehicleSessionGate)가 차량 선택 화면으로 돌아가게 한다.
      console.warn(
        "[SocketMqttBridge] MQTT vehicle session recovery gave up",
        { vehicleId, attempts: this.mqttSessionRecoveryAttempts },
      );
      this.patchVehicleSession({ recovering: false });
      this.clearVehicleSession();
      return;
    }

    this.mqttSessionRecovering = true;
    this.mqttSessionRecoveryAttempts += 1;
    // 복구 시도 중 — UI 가 콕핏 화면을 유지하도록 알린다.
    this.patchVehicleSession({ recovering: true });
    void this.recoverMqttVehicleSession(vehicleId).finally(() => {
      this.mqttSessionRecovering = false;
    });
  }

  private async recoverMqttVehicleSession(vehicleId: string): Promise<void> {
    try {
      await this.preconnectMqtt(vehicleId);
      this.attachPendingMqttSubscriptions();
      this.markVehicleTransportConnected("mqtt");
      this.mqttSessionRecoveryAttempts = 0;
      this.patchVehicleSession({ recovering: false });
      console.log("[SocketMqttBridge] MQTT vehicle session recovered", {
        vehicleId,
      });
    } catch (err) {
      // 브로커는 붙었지만 차량 세션이 아직 안 붙은 경우 — 잠시 후 재시도.
      console.warn(
        "[SocketMqttBridge] MQTT vehicle session recovery failed, retrying:",
        formatError(err).message,
      );
      this.clearMqttSessionRecoveryTimer();
      this.mqttSessionRecoveryTimer = setTimeout(() => {
        this.mqttSessionRecoveryTimer = null;
        this.scheduleMqttVehicleSessionRecovery();
      }, MQTT_SESSION_RECOVERY_RETRY_MS);
    }
  }

  private clearMqttSessionRecoveryTimer(): void {
    if (this.mqttSessionRecoveryTimer) {
      clearTimeout(this.mqttSessionRecoveryTimer);
      this.mqttSessionRecoveryTimer = null;
    }
  }

  private markVehicleTransportConnected(transport: VehicleTransport): void {
    if (!this.vehicleSession.vehicleId) {
      return;
    }
    this.patchVehicleSession({
      ...(transport === "mqtt"
        ? {
            mqttTransportState: "connected" as VehicleTransportState,
            mqttError: null,
          }
        : {
            signalingTransportState: "connected" as VehicleTransportState,
            signalingError: null,
          }),
    });
  }

  private markVehicleTransportFailed(
    transport: VehicleTransport,
    error: string,
  ): void {
    if (!this.vehicleSession.vehicleId) {
      return;
    }
    // 브로커가 살아있는데 차량 세션만 끊긴 경우 즉시 복구를 시도한다(빠른 리커버리 — 요청).
    if (transport === "mqtt") {
      setTimeout(() => this.scheduleMqttVehicleSessionRecovery(), 0);
    }
    this.patchVehicleSession({
      ...(transport === "mqtt"
        ? {
            mqttTransportState: "failed" as VehicleTransportState,
            mqttError: error,
          }
        : {
            signalingTransportState: "failed" as VehicleTransportState,
            remoteVideoReady: false,
            signalingError: error,
          }),
    });
  }

  private patchVehicleSession(
    patch: Partial<VehicleSessionSnapshot>,
  ): void {
    if (!this.vehicleSession.vehicleId && !patch.vehicleId) {
      return;
    }
    this.vehicleSession = this.normalizeVehicleSession({
      ...this.vehicleSession,
      ...patch,
    });
    this.emitVehicleSession();
  }

  private normalizeVehicleSession(
    snapshot: VehicleSessionSnapshot,
  ): VehicleSessionSnapshot {
    if (!snapshot.vehicleId) {
      return createEmptyVehicleSessionSnapshot();
    }

    const activeTransports: VehicleTransport[] = [];
    if (snapshot.mqttTransportState === "connected") {
      activeTransports.push("mqtt");
    }
    if (snapshot.signalingTransportState === "connected") {
      activeTransports.push("signaling");
    }

    const requestedCount = this.requestedVehicleTransports.length;
    const hasConnecting =
      snapshot.mqttTransportState === "connecting" ||
      snapshot.signalingTransportState === "connecting";
    const hasFailed =
      snapshot.mqttTransportState === "failed" ||
      snapshot.signalingTransportState === "failed";

    let sessionState: VehicleSessionState = "idle";
    if (activeTransports.length === 0) {
      if (hasConnecting) {
        sessionState = "connecting";
      } else if (hasFailed) {
        sessionState = "error";
      }
    } else if (
      requestedCount > 1 &&
      activeTransports.length === requestedCount &&
      !hasConnecting &&
      !hasFailed
    ) {
      sessionState = "connected";
    } else {
      sessionState = "partial";
    }

    return {
      ...snapshot,
      sessionState,
      activeTransports,
    };
  }

  private emitVehicleSession(): void {
    const snapshot = this.getVehicleSessionSnapshot();
    this.vehicleSessionListeners.forEach((listener) => {
      listener(snapshot);
    });
  }

  private emitState(): void {
    const snapshot = this.getConnectionSnapshot();
    this.stateListeners.forEach((listener) => {
      listener(snapshot);
    });
  }
}
